/**
 * /client-engagement/searches/:searchId — the Search Detail performance report.
 *
 * Thrive publishes no report endpoint, so the backend composes this from a
 * search's candidacies plus its stage history and caches it for 24 hours. First
 * visit to a search therefore answers 202 with a compose already enqueued; the
 * page polls, and the caption tells the reader when the numbers were made and
 * that they refresh daily rather than live.
 *
 * The header renders IMMEDIATELY on that first 202 — it comes from the Postgres
 * mirror, not the cache — so the page has a title and a status while it waits
 * rather than showing a bare spinner.
 *
 * The v38 prototype's full widget set now renders. Top Companies and Outreach
 * Activity were absent until 2026-08-20 on the premise that this report had
 * neither a contact→position join nor outreach data; both premises were wrong.
 * ONE Thrive call returns a search's candidacies with their contacts, positions
 * and companies attached (`include=candidate,recent_position,companies`), and
 * one more returns the search's outreaches (`GET /searches/{id}?include=outreaches`).
 * The report composes both.
 *
 * ALL CANDIDATES is served by its own endpoint, not by this payload. The report
 * is an aggregate — 1299 candidacies are ~790 KB of Thrive JSON and would not
 * fit the cached item — so the table pages against
 * `GET .../report/candidates`, which reads a separate gzipped nine-field
 * projection from the same cache. Filters and sort are server-side for the same
 * reason: doing them here means shipping every row to the browser, which is the
 * thing the endpoint exists to avoid.
 *
 * TABS (2026-08-13, third one 2026-08-20). The page carries three: Dashboard
 * (the report above), Search Progress (the per-stage-by-day charts that rebuild
 * the legacy nuAdvisory scoreboard — see `SearchProgressPanel` for the two
 * legacy bugs it deliberately does NOT reproduce) and Contact Lists (the imports
 * list scoped to this search). The header is common to all three, so the reader
 * never loses the search's name, id and status by switching. The active tab
 * lives in the URL (`?tab=progress`, `?tab=contacts`), which is this repo's
 * convention for anything worth linking to and the reason Back toggles tabs
 * rather than leaving the page.
 *
 * A FOURTH TAB (2026-10-06): Candidates — the All Candidates table that sat at
 * the bottom of the Dashboard, moved to its own tab with stage chips, the
 * rejection reason, days in stage, location and the People Lists pager (epic
 * `2026-10-06-search-candidates-reports`, sub-task 02). Behind the
 * `search-candidates` page flag, released on every stage on 2026-10-07; the
 * Dashboard's own copy of the table was deleted with that flip.
 *
 * Search Progress reads the SAME cached report as the Dashboard and makes no
 * request of its own, so the two tabs can never show figures composed at
 * different moments. That is also why the report-state banners are rendered by
 * both: a report that is still building is the reason BOTH tabs are empty, and a
 * tab that stayed silent about it would look broken rather than pending.
 *
 * The contacts tab renders `ImportsListView` — the imports feature's own
 * component — rather than a copy. That is a deliberate exception to the
 * boundary the Imports CTA in `SearchesTable` keeps (a URL, no imports code):
 * two lists of the same rows WILL drift, and the PRD chose one component over
 * two consistent-for-now ones. What travels across the boundary is a props
 * contract, not the imports feature's state.
 *
 * The tab markup follows this repo's ad-hoc pattern (`role="tablist"` +
 * `role="tab"` + `aria-selected` + a local `tabClass`), the same as
 * `SendClosingEmail`, `SlideCreator` and `SyncContacts`. There is deliberately
 * no `Tabs` primitive in `@nu-assist/ui`.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import type { SearchReportResponse } from "@nu-assist/contracts";
import { useMe } from "@nu-assist/shared-fe";
import { Button, NewBadge } from "@nu-assist/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Mail, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { type AvatarsClient, createAvatarsClient } from "../../../clients/avatars.client";
import { type BullseyeClient, createBullseyeClient } from "../../../clients/bullseye.client";
import {
  CANDIDATE_REPORT_KINDS,
  type CandidateReportsClient,
  createCandidateReportsClient,
} from "../../../clients/candidateReports.client";
import {
  type CompanyListsClient,
  createCompanyListsClient,
} from "../../../clients/companyLists.client";
import { type ImportsClient, createImportsClient } from "../../../clients/imports.client";
import {
  ReportNotReadyError,
  SearchNotFoundError,
  type SearchReportClient,
  createSearchReportClient,
} from "../../../clients/searchReport.client";
import { RetryButton } from "../../../components/RetryButton";
import { searchStatusClasses } from "../../../components/searches/searchStatus";
import { activeFlags } from "../../../config/featureFlags";
import { usePreviewModeIfMounted } from "../../../features/PreviewProvider";
import {
  type AgendaSubmissionError,
  agendaErrorMessage,
  submitAgendaForm,
} from "../../../features/agenda/submitAgendaForm";
import { CompanyListsView } from "../../../features/company-lists/CompanyListsView";
import { COMPANY_LISTS_PAGE_ID } from "../../../features/company-lists/paths";
import { pageVisible } from "../../../features/useFeatureAccess";
import { formatDate } from "../../../lib/format/date";
import { thriveSearchCandidatesUrl } from "../../../lib/thriveLinks";
import { announcementFor } from "../../../shell/leftMenuModel";
import { ImportsListView } from "../../research/imports/ImportsListView";
import { CandidatesTab, candidatesSortFromParams } from "./CandidatesTab";
import { ReportPanel as Panel } from "./ReportPanel";
import { SearchDetailKpis } from "./SearchDetailKpis";
import { SearchProgressPanel } from "./SearchProgressPanel";
import { type ExportChoice, SearchReportExportMenu } from "./SearchReportExportMenu";
import { BULLSEYE_PAGE_ID, BullseyeTab } from "./bullseye/BullseyeTab";
import { AdditionsLine } from "./charts/AdditionsLine";
import { FunnelBars } from "./charts/FunnelBars";
import { LabeledBars } from "./charts/LabeledBars";
import { OutreachLines } from "./charts/OutreachLines";
import { StageDonut } from "./charts/StageDonut";
import { viewFor } from "./charts/progressView";
import { bucketLabel, bucketOutreach } from "./charts/timeRange";
import { buildReportDocument, loadDocumentCss } from "./export/buildReportDocument";
import { currentCandidateFilters, describeFilters, documentFilename } from "./export/exportFilters";
import { rememberLastViewedSearch } from "./lastViewedSearch";
import { isTerminalReportError, useSearchReport } from "./useSearchReport";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";

// One client per app session (lazy-resolves `globalThis.fetch` at call time, so
// MSW interception still applies — see the client's note).
const defaultClient = createSearchReportClient({ apiBase: API_BASE });
const defaultImportsClient = createImportsClient({ apiBase: API_BASE });
const defaultAvatarsClient = createAvatarsClient({ apiBase: API_BASE });
const defaultCandidateReportsClient = createCandidateReportsClient({ apiBase: API_BASE });
const defaultCompanyListsClient = createCompanyListsClient({ apiBase: API_BASE });
const defaultBullseyeClient = createBullseyeClient({ apiBase: API_BASE });

/** The tabs, and the value each carries in `?tab=`. `companies` exists only
 *  while the Company Lists page is visible (its flag, released on every
 *  stage 2026-10-20);
 *  `bullseye` only while the Bullseye page is (its flag, released on every
 *  stage 2026-10-07); `candidates` only while
 *  `search-candidates` is (its flag, released on every stage 2026-10-07). */
type TabId = "dashboard" | "progress" | "candidates" | "contacts" | "companies" | "bullseye";

export interface SearchDetailPageProps {
  /** Injectable for tests (defaults to the module-shared typed client). */
  client?: SearchReportClient;
  /** Injectable for tests — drives the contacts tab's list. */
  importsClient?: ImportsClient;
  /** Injectable caller email for tests; defaults to `useMe()`. */
  meEmailOverride?: string;
  /** Injectable for tests — drives the Company Lists tab. */
  companyListsClient?: CompanyListsClient;
  /** Injectable for tests — drives the Bullseye tab. */
  bullseyeClient?: BullseyeClient;
  /** Injectable for tests — the Candidates tab's photos (`/avatars/*` reads).
   *  Defaults to the module-shared client; a test that renders the tab passes
   *  a stub so no photo is asked for over the network. */
  avatarsClient?: AvatarsClient;
  /** Injectable for tests — the Candidates tab's stored reports (sub-task 04). */
  candidateReportsClient?: CandidateReportsClient;
}

/**
 * The candidate projection was not `ready` when the export asked for it, so
 * there is no document to write.
 *
 * Carries the status rather than asserting one, because the two non-ready values
 * mean different things to a reader and this page distinguishes them everywhere
 * else: `generating` means wait, `unavailable` means nothing is coming without a
 * Refresh. An earlier version of this class was named for `generating` alone and
 * told a reader whose compose had never been enqueued to "wait for the table to
 * finish loading" — an instruction that would never come true.
 * (/pr-review #755 iter 3, Agent C I12.)
 *
 * Its own class rather than a boolean, so the export path CANNOT reach
 * `saveBlob` on this branch: the only way out is the `catch`.
 */
class ProjectionNotReadyError extends Error {
  readonly status: string;
  readonly retryAfterSeconds: number;

  constructor(status: string, retryAfterSeconds: number) {
    super(`The candidate projection is ${status}, not ready.`);
    this.name = "ProjectionNotReadyError";
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Save a fetched blob under the filename the server chose.
 *
 * The object URL is revoked after the click: unlike the presigned and data URLs
 * the imports and BCT downloads use, this one holds the whole file in memory
 * until it is released.
 */
function saveBlob(blob: Blob, filename: string) {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(href);
}

/**
 * `ID <n>` with a copy button and a link out to Thrive.
 *
 * The id is the value a recruiter reads over the phone and pastes back into My
 * Searches (which now matches it), so it is worth a one-click copy rather than
 * a triple-click drag. The button reports its own outcome — a clipboard write
 * rejects on a denied permission or an insecure context, and a copy that
 * silently did nothing is worse than one that says so.
 */
function SearchIdLine({ searchId }: { searchId: string }) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  async function copy() {
    try {
      await navigator.clipboard.writeText(searchId);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  return (
    <span data-testid="search-detail-id" className="flex items-center gap-1.5">
      ID{" "}
      <a
        href={thriveSearchCandidatesUrl(searchId)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="search-detail-thrive-id"
        title={`Open search ${searchId} in Thrive`}
        className="font-semibold text-accent-ink underline decoration-dotted underline-offset-2"
      >
        {searchId}
      </a>
      <button
        type="button"
        data-testid="search-detail-copy-id"
        onClick={() => void copy()}
        aria-label={`Copy search id ${searchId}`}
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-caption text-text-secondary hover:bg-accent-muted hover:text-accent-ink focus:outline-none focus:ring-1 focus:ring-accent"
      >
        {copyState === "copied" ? (
          <Check className="h-3 w-3" aria-hidden="true" />
        ) : (
          <Copy className="h-3 w-3" aria-hidden="true" />
        )}
        {copyState === "copied" ? "Copied" : copyState === "failed" ? "Copy failed" : "Copy"}
      </button>
    </span>
  );
}

export function SearchDetailPage({
  client,
  importsClient,
  meEmailOverride,
  companyListsClient,
  bullseyeClient,
  avatarsClient,
  candidateReportsClient,
}: SearchDetailPageProps = {}) {
  const activeClient = client ?? defaultClient;
  const activeImportsClient = importsClient ?? defaultImportsClient;
  const me = useMe();
  const meEmail = meEmailOverride ?? (me.status === "authenticated" ? me.data.email : "");
  const { searchId = "" } = useParams<{ searchId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // An unknown `?tab=` value reads as Dashboard rather than as an empty page:
  // the parameter is user-editable and arrives in shared links, and the report
  // is the answer to "what is this page" either way.
  const tabParam = searchParams.get("tab");
  // Company Lists (epic 2026-09-25-chopbook-company-lists) rides the same gate
  // as its Research page; while that is hidden, `?tab=companies` reads as
  // Dashboard like any other unknown value.
  const previewMode = usePreviewModeIfMounted();
  const companyListsVisible = pageVisible(
    activeFlags(),
    previewMode,
    "linkedin",
    COMPANY_LISTS_PAGE_ID
  );
  // The Bullseye (epic 2026-10-05-bullseye) the same way: hidden, its
  // `?tab=bullseye` reads as Dashboard.
  const bullseyeVisible = pageVisible(
    activeFlags(),
    previewMode,
    "client-engagement",
    BULLSEYE_PAGE_ID
  );
  // The Candidates tab (epic 2026-10-06-search-candidates-reports, sub-task
  // 02): the All Candidates table moved out of the Dashboard into its own tab,
  // behind the `search-candidates` page flag (released on every stage since
  // 2026-10-07). Hidden, `?tab=candidates` reads as Dashboard like any other
  // unknown value.
  const candidatesVisible = pageVisible(
    activeFlags(),
    previewMode,
    "client-engagement",
    "search-candidates"
  );
  const activeTab: TabId =
    tabParam === "contacts"
      ? "contacts"
      : tabParam === "progress"
        ? "progress"
        : tabParam === "candidates" && candidatesVisible
          ? "candidates"
          : tabParam === "companies" && companyListsVisible
            ? "companies"
            : tabParam === "bullseye" && bullseyeVisible
              ? "bullseye"
              : "dashboard";

  // The People List detail's Bullseye shortcut arrives with `?review=<id>`;
  // once the tab has switched the other lists off, the parameter goes, so a
  // reload or a later toggle is not overridden by it.
  const dropReviewParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        if (!prev.has("review")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("review");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  function selectTab(tab: TabId) {
    const next = new URLSearchParams(searchParams);
    // The default tab is the ABSENCE of the parameter, so the canonical URL for
    // a search stays the bare one and `?tab=dashboard` never has to be a second
    // spelling of it.
    if (tab === "dashboard") next.delete("tab");
    else next.set("tab", tab);
    // The candidate panel belongs to the Candidates tab: leaving it closes the
    // panel, so returning does not reopen one nobody asked for.
    if (tab !== "candidates") next.delete("candidate");
    // A push, not a replace: switching tabs is a navigation the reader can
    // reasonably expect Back to undo.
    setSearchParams(next);
  }

  const tabClass = (id: TabId) =>
    `px-4 py-2 text-body font-medium border-b-2 transition-colors ${
      activeTab === id
        ? "border-accent text-accent"
        : "border-transparent text-text-secondary hover:text-text hover:border-border"
    }`;

  const { query, pollingExhausted, retry } = useSearchReport({
    client: activeClient,
    searchId,
  });
  const payload: SearchReportResponse | undefined = query.data;

  // THE OUTREACH ROLLUP, and it is the ONLY derived series left here.
  //
  // The stage filter used to sit beside it and has moved INTO `StageDonut` /
  // `FunnelBars`. That was a bug fix, not tidying: `export/ReportDocument.tsx`
  // renders those same components to build the downloaded `.html`, and a filter
  // applied here reached the screen and not the document. The rule now travels
  // with the widget, so both callers get it.
  //
  // The bucket choice stays a CALL-SITE decision, because it genuinely differs
  // per surface: this tab is weekly and unchangeable, Search Progress lets the
  // reader pick week or month. That is presentation, not a domain rule, so it
  // does not belong inside the chart.
  //
  // Memoized, and declared ABOVE EVERY EARLY RETURN because it is a hook. The
  // page returns early for the loading, error and no-payload states; memoizing
  // further down next to the `{ search, report }` destructure would run a hook
  // on some renders and not others. Hence `payload?.report`.
  const outreachWeeks = useMemo(
    () => bucketOutreach(payload?.report?.outreach_activity ?? [], "week"),
    [payload]
  );

  // NO STAGE PALETTE IS BUILT HERE ANY MORE. This used to hold one colour
  // assignment for the donut and the funnel, built over the union of what they
  // draw, so the two agreed. It made them agree with each other and with
  // nothing else: the Search Progress tab draws a different set, the downloaded
  // report renders the donut with no page around it, and both therefore drew
  // the same stage in a different colour. `stageSeriesStyle` is a function of
  // the stage NAME (see `components/stages/chartPalette.ts`), so every widget reaches the
  // same answer with nothing threaded through this component.

  const [exporting, setExporting] = useState<ExportChoice | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Refresh: discard the cached report, then invalidate the query. The rebuild
  // is NOT this mutation's job — the next GET misses the cache, answers 202 and
  // the page's existing polling and "Building this report" banner take over, so
  // the progress a reader sees here is the one they see on a first visit.
  const queryClient = useQueryClient();
  const refreshMutation = useMutation({
    mutationFn: () => activeClient.deleteReport(searchId),
    // RETURNS the invalidate promise on purpose: TanStack awaits a promise
    // returned from `onSuccess`, which is what keeps `isPending` true across
    // the re-read and not just across the DELETE. Dropping the return (or
    // wrapping it in `void`) re-opens the double-press window the button's
    // third disabled condition now also covers.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["searches", "report", searchId] }),
    onError: (error: unknown) =>
      // No optimistic anything: the report on screen is still the one the
      // server has, and saying otherwise would invent a rebuild.
      //
      // A 404 is the one failure a retry cannot fix — the search left the
      // mirror between the page load and the press — so it gets the copy the
      // rest of the page uses for that status instead of "please try again".
      // No temporal claim ("anymore"), for the same reason the export and the
      // full-page error make none: one bare 404 covers "not synced yet" and
      // "gone", and three surfaces guessing differently about one status is
      // how this page contradicted itself before.
      toast.error(
        error instanceof SearchNotFoundError
          ? "That search isn’t in nu-assist, so there’s nothing to refresh."
          : "Couldn’t refresh this report. Please try again."
      ),
  });

  // AgendaGPT: fire the n8n form POST on click and report by toast. No
  // confirmation step (Trello #711 cl.3) — the request is cheap, reversible by
  // ignoring an email, and carries no input the reader could get wrong: the
  // search comes from the page and the address from the session. A dialog that
  // only ever restates those two was a keystroke between the recruiter and the
  // thing they already asked for.
  //
  // Fire-and-forget by design — the workflow emails the agenda, so there is
  // nothing here to navigate to or poll for. The button is the retry: on
  // failure the toast names the reason and the button is live again.
  const agendaMutation = useMutation({
    mutationFn: submitAgendaForm,
    onSuccess: () => {
      toast.success("Agenda request submitted");
    },
    onError: (error: AgendaSubmissionError) => {
      toast.error(agendaErrorMessage(error));
    },
  });

  /**
   * Build the document for the tab the reader is on, and save it.
   *
   * ONE DOCUMENT PER TAB. The Dashboard export carries the KPIs, the six panels
   * and every candidate; the Search Progress export carries the three progress
   * charts at the range and granularity ON SCREEN. They are different reports
   * over different data with different filters, and welding them together made
   * the provenance block describe neither clearly.
   *
   * `mode: "view"` means "as I have it filtered" — the candidate filters and
   * sort on the Candidates tab, the range and granularity on Progress. The
   * Dashboard offers only `"full"`, which ignores both.
   *
   * Nothing is saved unless everything arrived: a document missing its
   * candidates is a file a reader would trust and act on.
   */
  async function handleDocumentExport(mode: "full" | "view") {
    if (!report || !payload) return;
    const section = activeTab === "progress" ? "progress" : "dashboard";
    setExportError(null);
    setExporting(mode === "full" ? "html-full" : "html-view");
    try {
      // The candidate filters and the sort belong to the tab that SHOWS the
      // table — the Candidates tab, the only one since the Dashboard's panel
      // was deleted (2026-10-07). `selectTab` carries every param across tabs,
      // so without this gate a Dashboard "Current view" taken after leaving the
      // Candidates tab was filtered and ordered by controls no longer on screen
      // (PR #1009 iter 2, Agent A §2.4).
      const tableOnThisTab = activeTab === "candidates";
      const filters =
        tableOnThisTab && mode === "view" ? currentCandidateFilters(searchParams) : {};
      // "Current view" from the Candidates tab is ordered as the table is —
      // the tab's default (ladder descending) differs from the server's default
      // (`name`), and a "view" document in another order than the view is not
      // the view.
      const ordering =
        tableOnThisTab && mode === "view" ? candidatesSortFromParams(searchParams) : {};
      // The progress charts are the tab's OWN view object, not a re-derivation:
      // rebuilding them here is exactly how the first version came to ignore
      // `?from`, `?to` and `?bucket` and download a year while showing April.
      const progress =
        section === "progress"
          ? viewFor(
              report,
              mode === "view"
                ? {
                    from: searchParams.get("from"),
                    to: searchParams.get("to"),
                    bucket: searchParams.get("bucket"),
                  }
                : {}
            )
          : undefined;
      const [page, css] = await Promise.all([
        section === "dashboard"
          ? activeClient.listCandidates(searchId, {
              ...filters,
              ...ordering,
              all: true,
              // The SAME report kinds as the table (`CandidatesTab`), so a
              // filtered "Current view" lists the rows on screen.
              reportKinds: CANDIDATE_REPORT_KINDS,
            })
          : Promise.resolve(null),
        loadDocumentCss(),
      ]);
      // A 202 is a SUCCESS on this endpoint — `res.ok` spans 200-299 — and its
      // body is a well-formed response whose `items` are empty because the
      // projection has not been composed YET, not because the search has no
      // candidates. `searchReport.client.ts` says so in its own docstring:
      // "What it must NOT do is render the empty `items` as an empty table."
      // Saving it is worse than rendering it. An empty table on screen sits
      // next to a spinner and a poll; an empty table in a downloaded file is a
      // standalone artifact that a recruiter forwards to a client, and it
      // states — with the full authority of a document — that this search has
      // nobody in it. That is the exact failure this whole feature exists to
      // prevent, so it fails loudly instead.
      // (/pr-review #755 iter 1, Agent A blocking 3.)
      //
      // `!== "ready"` and NOT `=== "generating"`. The status is a three-valued
      // enum — `ready | generating | unavailable` — and BOTH of the non-ready
      // values mean "empty because nothing has been composed". `unavailable` is
      // what `service.py` answers on a cache miss when the compose could not be
      // enqueued (queue unconfigured, claim write raised, SQS send threw), and
      // it carries the same well-formed body with the same empty `items`.
      //
      // Gating on `generating` alone was the first version of this guard, and
      // it is a mistake this page has made before: an existing test in this
      // file records `pollingExhausted` being narrowed the same way, which left
      // the reader under "we're still trying" after the loop had stopped. The
      // table already had the right idiom — the Dashboard's All Candidates
      // panel (now `CandidatesTab.tsx`) gated its rows on `status === "ready"`
      // — under a comment saying why: an empty state over an uncomposed
      // projection is a claim about the data. Here that claim would be written
      // to a file.
      // (/pr-review #755 iter 2, Agent C C3.)
      if (page && page.status !== "ready") {
        throw new ProjectionNotReadyError(page.status, page.refresh_after_seconds ?? 0);
      }
      const html = buildReportDocument({
        css,
        section,
        // LOWERCASE ON PURPOSE, and raised twice in review. This is the
        // document DESCRIPTOR, in the same register as `documentFilename`'s
        // slug — not a heading. Its pair is "report", so re-casing only the
        // one half would ship "— Search Progress" beside "— report". The
        // headings inside the document are Title Case (Trello #729 cl.4); this
        // line is deliberately not one of them.
        title: `${payload.search.name} — ${section === "progress" ? "search progress" : "report"}`,
        search: payload.search,
        report,
        candidates: page?.items ?? [],
        candidatesTruncated: Boolean(page?.candidates_truncated),
        stale: Boolean(payload.stale),
        appliedFilters: mode === "view" ? describeFilters(filters) : [],
        // The Candidates tab's columns print only where the tab is visible,
        // so a stage with the flag off downloads the document it always did.
        candidateDetails: candidatesVisible,
        progress,
        generatedOn: new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
      });
      const htmlName = documentFilename(payload.search.name, mode, section);
      saveBlob(new Blob([html], { type: "text/html;charset=utf-8" }), htmlName);
    } catch (error) {
      setExportError(
        // THREE outcomes, not two, and `refresh_after_seconds` is what splits
        // the last pair — never the status alone. `schemas.py` says so twice:
        // "Branch on `status` and `refresh_after_seconds`, never on the code",
        // and the interval "is 0 when nothing this endpoint controls is going to
        // change the answer, and non-zero when the dispatch failed transiently
        // and the next request may succeed."
        //
        // The in-page `unavailable` banner 170 lines below already splits on it,
        // under a comment calling an administrator "premature" while the loop is
        // still retrying. An earlier version of THIS branch consulted only the
        // status and sent every `unavailable` reader to an administrator — doing
        // the thing the same file calls premature, on the transient half.
        // The last two sentences are byte-identical to the server-side export
        // path's own pair below, because they are the same two situations.
        // (/pr-review #755 iter 4, Agent A N8.)
        error instanceof ProjectionNotReadyError
          ? error.status === "generating"
            ? "This report is still being built. Wait for the table to finish loading, then export."
            : error.retryAfterSeconds > 0
              ? "This report isn’t ready to export yet. Try the export again in a few seconds."
              : "This report hasn’t been built and no build could be started, so there’s nothing to export yet. Ask an administrator to check the report worker."
          : error instanceof SearchNotFoundError
            ? "That search isn’t in nu-assist, so there’s nothing to export."
            : "Couldn’t build this report document. Please try again."
      );
    } finally {
      setExporting(null);
    }
  }

  async function handleExport(format: ExportChoice) {
    if (format === "html-full") return handleDocumentExport("full");
    if (format === "html-view") return handleDocumentExport("view");
    setExportError(null);
    setExporting(format);
    try {
      const file = await activeClient.exportReport(searchId, format);
      saveBlob(file.blob, file.filename);
    } catch (error) {
      // Four outcomes, and only two of them are worth trying again:
      //   409 WITH a `Retry-After` — asking again may help. Deliberately says no
      //     more than that: the header covers a compose that IS running and a
      //     dispatch that failed transiently, and the API distinguishes them only
      //     in `detail`, which is prose for a developer rather than UI copy. The
      //     earlier version read the header as "a compose is running" and told the
      //     reader their report was "still being built" over a report nothing was
      //     building — the same class of false claim the 404 copy below removed.
      //     Which case it is does surface, one poll later, in the `generating` /
      //     `unavailable` banner further down the page;
      //   409 WITHOUT one — no compose is running and none could be started, so a
      //     timer changes nothing. The API withholds the header precisely so a
      //     client can tell this half apart;
      //   404 — the search is not in the mirror, so retrying is a loop the user
      //     cannot win. Deliberately makes no claim about WHY: the same bare 404
      //     covers "not synced yet" and "gone", and the read path's copy above
      //     must not contradict this one;
      //   anything else — transient, retry is the right advice.
      setExportError(
        error instanceof ReportNotReadyError
          ? error.retryAfterSeconds > 0
            ? "This report isn’t ready to export yet. Try the export again in a few seconds."
            : "This report hasn’t been built and no build could be started, so there’s nothing to export yet. Ask an administrator to check the report worker."
          : error instanceof SearchNotFoundError
            ? "That search isn’t in nu-assist, so there’s nothing to export."
            : "Couldn’t download this report. Please try again."
      );
    } finally {
      setExporting(null);
    }
  }

  // Remembered so the sidebar's parameter-less "Search Detail" item can land
  // somewhere. Only on a real response — an id that 404s is not worth returning
  // to.
  useEffect(() => {
    if (payload?.search?.id_thrive) {
      rememberLastViewedSearch(payload.search.id_thrive);
    }
  }, [payload?.search?.id_thrive]);

  // Only a FIRST load that failed gets the full-page error. In TanStack v5
  // `isError` stays true while `data` from an earlier successful fetch is still
  // cached, so branching on it alone threw away a report the user was reading
  // the moment one background poll 500'd — and put it back on the next one.
  //
  // A TERMINAL failure is the exception: a 404 means the search left the mirror,
  // and it must take over the page even with a report in hand. Softening that
  // into "couldn't refresh just now, the figures below are the last ones we
  // loaded" tells the reader stale when the truth is gone.
  const terminalError = isTerminalReportError(query.error);
  if (terminalError || (query.isError && !payload)) {
    const notFound = query.error instanceof SearchNotFoundError;

    return (
      <div data-testid="search-detail-error" className="flex flex-col gap-4 p-8">
        {/* `alert`, not `status`: this took over the page from whatever the
            reader had, so it interrupts. */}
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error"
        >
          <span>
            {/* No temporal claim: a bare 404 carries no discriminator, so
                "yet" and "anymore" were both guesses — and the export path 34
                lines up was guessing the opposite about the same status. Name
                both possibilities instead of picking one. */}
            {notFound
              ? "That search isn’t in nu-assist. It may be new in Thrive and not yet synced, or it may have left the catalog — the nightly sync reconciles either way."
              : "Couldn’t load this search’s report. Please try again."}
          </span>
          {/* "Please try again" needs something to press. This is where a cold
              search whose endpoint keeps 500-ing lands: polling never starts
              (there is no `generating` payload to poll), so the budget's own
              banner cannot cover it, and the only other exit was a browser
              reload. Withheld for the 404, which retrying cannot fix. */}
          {notFound ? null : <RetryButton onClick={retry} busy={query.isFetching} tone="error" />}
        </div>
        <Link to="/client-engagement/searches" className="text-body text-accent-ink underline">
          Back to My Searches
        </Link>
      </div>
    );
  }

  if (!payload) {
    return (
      <div data-testid="search-detail-loading" className="p-8">
        <p className="text-body text-text-secondary">Loading search…</p>
      </div>
    );
  }

  const { search, report } = payload;

  const generating = payload.status === "generating";
  const unavailable = payload.status === "unavailable";

  // The report's own state, rendered by BOTH report-backed tabs. These four
  // banners answer "why is there nothing here", and the answer is identical on
  // Dashboard and Search Progress because they read the same cached item. The
  // EXPORT error deliberately stays out: it belongs to a control only the
  // Dashboard has, and a failure it reported on a tab without the button would
  // be an error message with no visible cause.
  const reportStateBanners = (
    <>
      {pollingExhausted ? (
        // The poll budget ran out while the payload was still asking to be
        // polled — which is TWO states, not one. A compose that never finished,
        // and a dispatch that kept failing. Gating this on `generating` left the
        // second one under "we're still trying" after the loop had stopped
        // trying, with the `RetryButton` as its only exit and nothing saying it
        // was now the reader's move. Everything reachable from here is manual, so
        // say that plainly.
        <div
          data-testid="search-detail-poll-exhausted"
          className="flex flex-wrap items-center gap-3 rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
        >
          <output>
            {unavailable
              ? "We couldn’t start building this report, and we’ve stopped retrying automatically."
              : "This report is taking longer than expected, so we stopped checking automatically."}
          </output>
          <RetryButton onClick={retry} busy={query.isFetching} label="Check again" />
        </div>
      ) : generating ? (
        <div
          data-testid="search-detail-generating"
          className="flex flex-wrap items-center gap-3 rounded-md border border-accent-muted bg-accent-muted px-4 py-3 text-body text-text"
        >
          <output>
            {report
              ? "Refreshing this report from Thrive. The numbers below are the previous version."
              : "Building this report from Thrive. It usually takes a few seconds."}
          </output>
          <RetryButton onClick={retry} busy={query.isFetching} label="Check now" tone="accent" />
        </div>
      ) : null}

      {unavailable && !pollingExhausted ? (
        <div
          data-testid="search-detail-unavailable"
          className="flex flex-wrap items-center gap-3 rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
        >
          <output>
            {/* `unavailable` is two situations, and the interval is what tells
                them apart — a non-zero one means the dispatch errored and the
                loop is still retrying, where sending the reader to an
                administrator is premature. Once the budget is spent that is no
                longer true of either, which is what the banner above is for. */}
            {payload.refresh_after_seconds > 0
              ? "We couldn’t start building this report just now, and we’re still trying."
              : "This report hasn’t been built yet and no build could be started. Ask an administrator to check the report worker."}
          </output>
          {/* Polling stops on the terminal half of this state, so without this
              the reader has no exit at all short of a page reload. */}
          <RetryButton onClick={retry} busy={query.isFetching} />
        </div>
      ) : null}
    </>
  );

  // The banner a failed POLL raises, as opposed to a report state. Also common
  // to both report tabs: the figures it warns about are on both.
  const staleErrorBanner = query.isError ? (
    // A poll failed while a report is already on screen. Say so and offer the
    // retry, but do NOT replace the numbers the reader is looking at — they
    // are the last thing the server actually confirmed.
    //
    // `<output>` carries `role="status"` and a polite live region natively,
    // which is what biome's useSemanticElements wants. Non-destructive by
    // design — the figures below do not move — which is exactly why a
    // screen-reader user would otherwise get no signal that what they are
    // reading is no longer current.
    //
    // The region wraps the MESSAGE only, and the button is a sibling: this
    // banner and its three neighbours above all carry a `RetryButton` whose
    // label flips on `isFetching`, and `role="status"` is implicitly
    // `aria-atomic="true"` — so with the button inside, one poll tick
    // re-announced the whole banner. On a cold search that is the same
    // sentence read out up to 80 times over two minutes, which is worse than
    // the plain `<div>` the live regions replaced.
    <div
      data-testid="search-detail-stale-error"
      className="flex flex-wrap items-center gap-3 rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
    >
      <output>
        Couldn’t refresh this report just now. The figures below are the last ones we loaded.
      </output>
      {/* Not the default "Try again": this banner can be on screen at the same
          time as the `unavailable` one above (a poll that errors after an
          `unavailable` answer keeps the payload), and two buttons with the same
          accessible name in one view are ambiguous to anyone listing them.

          It said "Refresh" until 2026-08-13, when the tab above grew a Refresh
          button of its own — and THOSE two are different actions: this one
          re-reads a report the server already has, that one throws the cached
          report away and makes the worker build a new one from Thrive. Sharing a
          name would have been the worse collision of the two, because both can be
          on screen together and pressing the wrong one costs a ~10s rebuild.
          "Reload" keeps this banner's meaning — fetch the figures again — and
          stays distinct from the four other retry labels this page can render. */}
      <RetryButton onClick={retry} busy={query.isFetching} label="Reload" />
    </div>
  ) : null;

  // Said once, by whichever report tab is open. The Dashboard used to own this
  // sentence; Search Progress is composed from the same item at the same moment,
  // so repeating it there is the truth rather than a copy.
  const generatedCaption = report ? (
    <p data-testid="search-detail-caption" className="text-caption text-text-secondary">
      Report generated {formatDate(report.generated_at)}. It refreshes at most once a day.
    </p>
  ) : null;

  const staleNotice = payload.stale ? (
    <p data-testid="search-detail-stale" className="text-caption text-warning-ink">
      Showing the last completed report — it is past its daily refresh.
    </p>
  ) : null;

  // Rendered on BOTH the Dashboard and the Search Progress tabs.
  //
  // The Progress tab deliberately had no toolbar until 2026-08-21, on the
  // reasoning that Refresh and Export both act on the report as a whole and
  // both already lived one tab away. That held while the export was
  // dashboard-shaped. It stopped holding the moment the export began to
  // CONTAIN the Progress tab: a reader looking at Search Progress and wanting
  // to download what they are looking at should not have to know it lives
  // somewhere else. Reconsidered, not overlooked.
  //
  // The error surface below travels WITH the toolbar. A trigger whose failure is
  // invisible from where it was pressed is the defect the 2026-08 move narrowed
  // on the contacts tab; rendering the toolbar on a second tab without moving
  // this too would re-open it there.
  const exportErrorBanner = exportError ? (
    <output
      data-testid="search-detail-export-error"
      className="rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error"
    >
      {exportError}
    </output>
  ) : null;

  const reportToolbar = (
    <div className="flex items-center justify-end gap-2">
      {/* Export sits beside Refresh. This toolbar is NO LONGER dashboard-only:
            Jose asked for the download control and Refresh on Search Progress
            too, so `reportToolbar` renders on both tabs and the menu names
            whichever one it was pressed on. Refresh still rebuilds the whole
            REPORT — the two tabs share one — so it means the same thing from
            either. Moved here from the page header in 2026-08.

            The move NARROWS a silent-failure path — it does not close it,
            and closing it was never the reason for the move.
            `search-detail-export-error` has always rendered inside this
            branch, so an export triggered from the contacts tab had nowhere
            at all to report a failure. Trigger and outcome now share a tab
            WHILE THE READER STAYS ON IT. `handleExport` is async and a tab
            switch unmounts this branch, not the page, so an export still in
            flight when the reader leaves still fails into an unmounted
            `<output>`, and the banner then mounts stale on return — nothing
            clears `exportError` except the next `handleExport`. Both are
            pre-existing behaviours, unchanged by this move; closing the
            window takes a `toast.error` in the catch (as Refresh and
            AgendaGPT above already do) plus a clear in `selectTab`, tracked
            as a follow-up. The error channel is orthogonal to the placement,
            so that fix composes with this one rather than replacing it.

            Known trade: `useSearchReport` runs with no tab gate, so the
            export's HAPPY path did work from the contacts tab and that reach
            is gone. Accepted — a control whose errors are invisible from
            where you pressed it is worse than one you reach in one more
            click.

            Keyed off `report`, not off `status`: a stale report is
            exportable (the file says it is stale), whereas a first visit has
            nothing to put in a file at all. */}
      <SearchReportExportMenu
        section={
          activeTab === "progress"
            ? "progress"
            : activeTab === "candidates"
              ? "candidates"
              : "dashboard"
        }
        disabled={!report}
        busyFormat={exporting}
        onSelect={handleExport}
      />
      {/* Not on the CONTACTS tab: it refreshes the REPORT, and contacts have
            none to refresh. It IS on both report tabs — Dashboard and Search
            Progress are composed from the same item, so Refresh means the same
            thing pressed from either. (It read "Dashboard-only" until
            /pr-review #755 iter 3, Agent A N7 — true before the toolbar moved
            to Progress, and quietly false after.)
            Disabled while one is already being built — a
            delete landing mid-compose is harmless (the worker's write
            re-creates the item) but it would not shorten the wait, so the
            press would buy the reader nothing they can see.

            `query.isFetching` is the THIRD condition, and it is belt to the
            mutation's braces: between the DELETE answering 204 and the
            re-read settling, the payload on screen is still the pre-delete
            `ready` one, so `generating` is false. `isPending` covers that
            window TODAY only because `onSuccess` returns the invalidate
            promise for TanStack to await — a coupling nothing about the
            button makes visible. This condition is true of the window
            itself, so the guard survives that return going away. */}
      <Button
        variant="secondary"
        data-testid="search-detail-refresh"
        onClick={() => refreshMutation.mutate()}
        disabled={generating || refreshMutation.isPending || query.isFetching}
        title={
          generating ? "This report is already being rebuilt." : "Rebuild this report from Thrive."
        }
      >
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
        {refreshMutation.isPending ? "Refreshing…" : "Refresh"}
      </Button>
    </div>
  );

  return (
    <div data-testid="search-detail-page" className="flex flex-col gap-4 p-8">
      <nav className="text-caption text-text-secondary">
        <Link to="/client-engagement/searches" className="hover:text-accent-ink">
          My Searches
        </Link>
        <span className="px-1">/</span>
        <span className="text-text">Search Detail</span>
      </nav>

      <header className="rounded-md border border-border bg-surface px-4 py-4">
        <div className="flex items-start justify-between gap-4">
          <h1 data-testid="search-detail-name" className="text-display text-text">
            <a
              href={thriveSearchCandidatesUrl(search.id_thrive)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="search-detail-thrive-title"
              title={`Open search ${search.id_thrive} in Thrive`}
              className="hover:underline focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {search.name}
            </a>
          </h1>
          <div className="flex items-center gap-2">
            {/* Lives in the HEADER, so it is available from either tab: asking
                for an agenda is about the search, not about whichever view of
                it happens to be open.

                Disabled without an authenticated session because the email is
                the whole output channel — the workflow mails the agenda to the
                address sent with the request, and submitting an empty one
                produces a run that succeeds and reaches nobody. */}
            <Button
              variant="secondary"
              data-testid="search-detail-agenda-gpt"
              onClick={() => agendaMutation.mutate({ searchId: search.id_thrive, email: meEmail })}
              disabled={!meEmail || agendaMutation.isPending}
              title={
                meEmail
                  ? undefined
                  : "Sign in again to request an agenda — we need your email to send it to."
              }
            >
              {/* The envelope, like the My Searches row's AgendaGPT action
                  (Lauren via Jared, 2026-10-07). */}
              <Mail className="h-4 w-4" aria-hidden="true" />
              AgendaGPT
            </Button>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-caption text-text-secondary">
          <SearchIdLine searchId={search.id_thrive} />
          <span>
            Status{" "}
            <span className={searchStatusClasses(search.status)}>{search.status ?? "—"}</span>
          </span>
          {search.client_company_name ? (
            <span>
              Client <strong className="text-text">{search.client_company_name}</strong>
            </span>
          ) : null}
          <span>
            Candidates in Thrive{" "}
            {/* Null means Thrive's counter was unusable (denormalized, can go
                negative). Em dash is this feature's "we do not know" — same as
                `days_open` below and the list table. Never 0: it reads as a
                fact, and the searches that hit this have had dozens. */}
            <strong className="text-text" data-testid="search-detail-candidacies">
              {search.candidacies_count ?? "—"}
            </strong>
          </span>
          <span>
            Created <strong className="text-text">{formatDate(search.thrive_created_at)}</strong>
          </span>
          <span>
            Days open{" "}
            <strong className="text-text" data-testid="search-detail-days-open">
              {search.days_open ?? "—"}
            </strong>
          </span>
        </div>
      </header>

      <div className="flex border-b border-border" role="tablist" data-testid="search-detail-tabs">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "dashboard"}
          data-testid="search-detail-tab-dashboard"
          className={tabClass("dashboard")}
          onClick={() => selectTab("dashboard")}
        >
          Dashboard
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "progress"}
          data-testid="search-detail-tab-progress"
          className={tabClass("progress")}
          onClick={() => selectTab("progress")}
        >
          Search Progress
        </button>
        {candidatesVisible && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "candidates"}
            data-testid="search-detail-tab-candidates"
            className={tabClass("candidates")}
            onClick={() => selectTab("candidates")}
          >
            Candidates
            {/* Released 2026-10-07; `announcementFor` owns the rule (released
                AND inside `CANDIDATES_TAB_NEW_BADGE`), as the sidebar's pills. */}
            {announcementFor("search-candidates") === "new" ? (
              <NewBadge className="ml-1.5" data-testid="search-detail-tab-candidates-new" />
            ) : null}
          </button>
        )}
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "contacts"}
          data-testid="search-detail-tab-contacts"
          className={tabClass("contacts")}
          onClick={() => selectTab("contacts")}
        >
          People Lists
        </button>
        {companyListsVisible && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "companies"}
            data-testid="search-detail-tab-companies"
            className={tabClass("companies")}
            onClick={() => selectTab("companies")}
          >
            Company Lists
          </button>
        )}
        {bullseyeVisible && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "bullseye"}
            data-testid="search-detail-tab-bullseye"
            className={tabClass("bullseye")}
            onClick={() => selectTab("bullseye")}
          >
            Bullseye
            {announcementFor("bullseye") === "new" ? (
              <NewBadge className="ml-1.5" data-testid="search-detail-tab-bullseye-new" />
            ) : null}
          </button>
        )}
      </div>

      {/* A ternary chain, so the tabs that are not showing are UNMOUNTED and
          their local state (the imports list's page, tag chip, owner scope and
          search box; the progress charts' hidden-series set) is discarded. That
          is deliberate, not an oversight: what this page persists is the TAB —
          which is in the URL, shareable and Back-able — and not the reading
          position inside it. The alternative is lifting four pieces of the
          imports feature's state into this page, which is exactly the boundary
          the props contract exists to avoid. `SearchDetailPage.test.tsx` pins
          the reset, so changing this is a decision rather than a diff. */}
      {activeTab === "contacts" ? (
        <div data-testid="search-detail-contacts" className="flex flex-col gap-4">
          {/* The imports list, pinned to this search. `initialOwner="all"` for
              the same reason the imports page adopts it when it arrives with a
              search filter: the imports for a search usually belong to somebody
              else on the team, so starting at You would show an empty list on a
              tab that promises this search's contacts.

              No `onClearSearchFilter`, which is what withholds the chip: the
              filter here is the page's own identity, and a control offering to
              remove it would turn this tab into the firm-wide list. */}
          <ImportsListView
            client={activeImportsClient}
            meEmail={meEmail}
            searchIdThrive={search.id_thrive}
            initialOwner="all"
            title="People Lists"
            headingLevel="h2"
          />
        </div>
      ) : activeTab === "companies" ? (
        <div data-testid="search-detail-companies" className="flex flex-col gap-4">
          {/* The same shape as People Lists next door: the lists of THIS
              search, with New list / Import CSV (a list always starts inside a
              search, 00 Q1). */}
          <CompanyListsView
            client={companyListsClient ?? defaultCompanyListsClient}
            searchId={search.id_thrive}
            title="Company Lists"
            headingLevel="h2"
            // Everyone, for the reason People Lists next door starts there: a
            // search's lists are usually the team's, not the reader's.
            meEmail={meEmail}
            initialOwner="all"
          />
        </div>
      ) : activeTab === "bullseye" ? (
        <div data-testid="search-detail-bullseye">
          {/* Keyed by search: moving to another search must not carry this
              one's unsaved boundaries or switched-off lists across. */}
          <BullseyeTab
            key={search.id_thrive}
            client={bullseyeClient ?? defaultBullseyeClient}
            avatarsClient={avatarsClient ?? defaultAvatarsClient}
            searchId={search.id_thrive}
            peopleListsHref="?tab=contacts"
            onlyReviewId={searchParams.get("review")}
            onReviewApplied={dropReviewParam}
          />
        </div>
      ) : activeTab === "candidates" ? (
        <>
          {/* The same toolbar and banners as the two report tabs: the table is
              served from the SAME compose as the report, so "building",
              "stale" and Refresh mean the same thing here. Export carries the
              Dashboard document (which includes every candidate) — see
              `handleDocumentExport`. */}
          {reportToolbar}
          {exportErrorBanner}
          {staleErrorBanner}
          {reportStateBanners}
          {report ? (
            <>
              {staleNotice}
              <div data-testid="search-detail-candidates" className="flex flex-col gap-4">
                {/* Mounted inside the `report` branch, as the Dashboard panel
                    was, so a composing search shows one "building" banner
                    rather than two. The chips read the report's own stage
                    distribution; the rows come from the candidates endpoint. */}
                <CandidatesTab
                  searchId={search.id_thrive}
                  client={activeClient}
                  avatarsClient={avatarsClient ?? defaultAvatarsClient}
                  stageDistribution={report.current_stage_distribution}
                  reportsClient={candidateReportsClient ?? defaultCandidateReportsClient}
                  searchName={search.name}
                />
              </div>
              {generatedCaption}
            </>
          ) : null}
        </>
      ) : activeTab === "progress" ? (
        <>
          {reportToolbar}
          {exportErrorBanner}
          {staleErrorBanner}
          {reportStateBanners}
          {report ? (
            <>
              {staleNotice}
              {/* NO `generatedCaption` ON THIS TAB, and its absence is the
                  change rather than an oversight. Jared, 2026-08-25: "instead
                  of this note this should be the note report generated date
                  auto refreshes daily. Button to refresh on demand." The panel
                  now renders that note itself, at its own foot, with the button
                  in it — so leaving the caption here would print two sentences
                  about the same fact one line apart. The Dashboard tab below
                  still renders it: nothing there moved.

                  The mutation stays HERE because the client and the search id
                  do, and a second `deleteReport` call site is a second place
                  for the invalidate to drift from. The panel takes the
                  callback, not the client. */}
              <SearchProgressPanel
                report={report}
                onRefresh={() => refreshMutation.mutate()}
                // The same three conditions the toolbar's Refresh uses, and for
                // the same reasons — see the note on that button. Both press the
                // same mutation, so one of them staying live while the other
                // greys out would be two controls disagreeing about one state.
                refreshing={generating || refreshMutation.isPending || query.isFetching}
              />
            </>
          ) : null}
        </>
      ) : (
        <>
          {reportToolbar}

          {exportErrorBanner}

          {staleErrorBanner}

          {reportStateBanners}

          {report ? (
            <>
              {staleNotice}

              <SearchDetailKpis kpis={report.kpis} />

              {/* ONE PALETTE ACROSS BOTH, built from the union rather than by
                  each widget from its own data. Jared, 2026-08-25: *"the
                  reference in the chart should be the same like the current
                  [stage]… we also shouldn't have two like this color and this
                  color is the same."* The donut draws where candidates ARE and
                  the funnel draws the rungs they REACHED, which are different
                  sets; two independent assignments would each be internally
                  collision-free and still disagree with each other about
                  `Identified`. */}
              {/* EVERY WORD OF A CHART TITLE IS CAPITALIZED, prepositions
                  included. Jared, 2026-08-25, reading `Highest stage funnel`
                  out loud: *"all chart titles should just be capitalized,
                  first letter"* -- and he spelled it *"capital S, capital F"*,
                  naming the SECOND and THIRD words. Reading that as "first
                  letter of the title" is the trap: every title already
                  satisfied it, so the item was reported done in August with
                  nothing changed. Sentence case here is a regression, not a
                  tidy-up. The same rule holds on the Search Progress tab, in
                  `export/ReportDocument.tsx` and in the backend's
                  `search_report/domain_export.py` -- tooltips and subtitles
                  are prose and stay as they are. */}
              <div className="grid gap-4 lg:grid-cols-2">
                {/* SOURCE AND JURY MEMBER ARE NOT DRAWN, and the filter lives
                    here rather than in the report because the payload should
                    stay complete: the stored document is what the export, the
                    KPIs and any future widget read, and one of them wanting
                    these rows back must not require regenerating every cached
                    report. Jared, 2026-08-27: *"some of these are candidates in
                    the search, which is what we care about, other ones are not
                    candidates."* The rule and the full reasoning live in
                    `charts/seriesDefaults.ts`. */}
                <Panel
                  title="Current Stage"
                  // THE DENOMINATOR IS NAMED, because it is no longer the KPI
                  // sitting directly above this chart. Dropping two stages from
                  // the ring without saying so would leave a reader comparing
                  // these percentages against `total_candidacies` and finding
                  // they disagree.
                  tooltip="Where candidates are now — one slice per current stage, as a share of the candidates drawn here. Source and Jury Member are excluded: they are not candidates on the search."
                  testId="current-stage"
                >
                  {/* The wire value goes in unfiltered — `StageDonut` drops
                      the not-a-candidate stages and derives its own
                      denominator. See the note on the outreach rollup above
                      for why this rule lives in the component. */}
                  <StageDonut buckets={report.current_stage_distribution} />
                </Panel>
                <Panel
                  title="Highest Stage Funnel"
                  // Carries what the key stopped saying. Jared asked for
                  // "color dot word number percent" and *"we don't need of the
                  // widest stage"*, so the denominator moved here rather than
                  // being dropped -- a bare percentage beside a rung is a
                  // number with no question attached.
                  tooltip="Furthest stage each candidate reached, counted cumulatively. Each percentage is that rung's share of the widest one. Source and Jury Member are excluded: they are not candidates on the search."
                  testId="highest-stage-funnel"
                >
                  <FunnelBars
                    steps={report.highest_stage_funnel}
                    emptyMessage="No candidate has progressed past sourcing on this search."
                  />
                </Panel>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                {/* THE CHART STAYS. The Trello card carried "remove Rejection
                    reasons"; the 08-25 transcript does not. Jared named this
                    widget by its subtitle (*"share of the 24 candidates"*) and
                    then said *"this one can be a tool tip"* -- the removal was
                    read back out of a summary Jose spoke, not out of anything
                    Jared asked for. Confirmed by Jose 2026-08-27: the chart is
                    unchanged and gains the tooltip, nothing else. */}
                <Panel
                  title="Rejection Reasons"
                  tooltip={`Share of the ${report.rejected_with_reason_total} candidates with a recorded reason — not of every candidate on the search.`}
                  testId="rejection-reasons"
                >
                  <LabeledBars
                    buckets={report.rejection_reasons}
                    emptyMessage="No rejection reasons recorded on this search."
                    testId="rejection-bars"
                  />
                  {report.rejection_reasons_truncated ? (
                    <p className="mt-2 text-caption text-text-secondary">
                      Showing the most common reasons only.
                    </p>
                  ) : null}
                </Panel>
                <Panel
                  title="Additions Over Time"
                  // WEEKLY, AND NO CONTROL TO CHANGE IT. Jose asked on the call
                  // whether the reader should be able to switch the rollup;
                  // Jared, 2026-08-25: *"No, you can just make it by week and
                  // no unchangeable for now."* The copy therefore states the
                  // bucket rather than offering one.
                  tooltip="Candidates added per week, counted from the date each candidacy was created in Thrive."
                  testId="additions"
                >
                  <AdditionsLine
                    weeks={report.additions_by_week}
                    emptyMessage="No candidate has a recorded creation date on this search."
                  />
                  {report.additions_outside_window_count > 0 ? (
                    <p className="mt-2 text-caption text-text-secondary">
                      {report.additions_outside_window_count} added before this window.
                    </p>
                  ) : null}
                </Panel>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <Panel
                  title="Where These Candidates Have Worked"
                  // NOT "where the candidates work". `_top_companies` counts the
                  // `companies` relationship, which is each candidate's whole
                  // CAREER HISTORY — ~4.2 companies per candidate on real data.
                  // The current-employer reading is a different question with a
                  // disjoint answer: on search 4379 the two top-tens share zero
                  // companies — this widget leads with Goldman Sachs at 23,
                  // while the top current employers are OpenAI, LeafLink and
                  // Dandy at 3 apiece. (Those are read off a composed report;
                  // counting raw relationship links instead gives 33, which is
                  // the same measurement mistake iter 2 caught in
                  // `_top_companies`' docstring — see it for the arithmetic.)
                  // Saying
                  // "work" invited the reader to check it against the Company
                  // column in the table below, which shows current employer.
                  // Which reading Jared wants is an open question in the epic
                  // PRD; until it is answered the label must describe what is
                  // actually plotted. Found by /pr-review iter 1.
                  tooltip="Companies these candidates have worked at, by number of candidates. Career history, not current employer — the Company column in the table below is the current one."
                  testId="top-companies"
                >
                  {/* `LabeledBars`, not a new component: this is the same
                      ranked-horizontal-bar shape as Rejection Reasons, and a
                      second implementation of it would be a second place for
                      the scaling and the empty state to be wrong.

                      The label carries no link. The aggregate is a
                      `{label, count, pct}` bucket built from Thrive's company
                      ENTITIES, but only their NAME survives into the cached
                      item — there is no company id and no `website_url` on the
                      wire to link to. Adding one is a report-shape change and a
                      schema bump, tracked as a follow-up rather than smuggled
                      in here. */}
                  <LabeledBars
                    buckets={report.top_companies ?? []}
                    emptyMessage="No company is recorded for the candidates on this search."
                    testId="top-companies-bars"
                  />
                </Panel>
                <Panel
                  title="Outreach Activity"
                  // WEEKLY, AND NO CONTROL TO CHANGE IT — the same shape as
                  // "Additions Over Time" beside it. Jose asked on the call
                  // whether this chart should follow; Jared, 2026-08-27:
                  // *"Yeah, it should be. Let's make that weekly, too."* The
                  // copy states the bucket rather than offering one.
                  tooltip="Outreaches per week, split by contact method."
                  testId="outreach-activity"
                >
                  {/* ROLLED UP HERE, NOT IN THE BACKEND, and that is deliberate.
                      `outreach_activity` is read at day resolution by the Search
                      Progress tab, whose week/month selector Jared approved on
                      08-25; making the field weekly would take that away. This
                      differs from `additions_by_month -> additions_by_week`,
                      which could move server-side because nothing else consumed
                      it. `bucketOutreach` is the same helper Search Progress
                      uses, so the two tabs cannot disagree about where a week
                      starts. */}
                  <OutreachLines
                    days={outreachWeeks}
                    pointLabel={(day) => bucketLabel(day, "week")}
                    pointNoun="week"
                    emptyMessage="No outreach recorded on this search."
                  />
                  {(report.outreach_outside_window_count ?? 0) > 0 ? (
                    <p className="mt-2 text-caption text-text-secondary">
                      {report.outreach_outside_window_count} logged before this window.
                    </p>
                  ) : null}
                </Panel>
              </div>

              {generatedCaption}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
